import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { type Event } from 'nostr-tools';
import {
  Heart, MessageCircle, Repeat2, User, Play, Pause,
  Volume2, VolumeX, ChevronUp, ChevronDown, Plus, X, Loader2,
  Camera, Send, Film, Coins, Wallet, Check, AlertCircle,
  Flag, ShieldX, Trash2, AlertTriangle, Shield, Rocket, Clock,
  TrendingUp, ChevronRight, Share2, Link2,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish, getRelays } from '../hooks/useNostrPublish';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { uploadVideo } from '../lib/imageUpload';
import { timeAgo } from '../lib/utils';
import { BCHLogo } from '../components/Layout';
import VerifiedBadge from '../components/VerifiedBadge';
import { useTipWallet } from '../hooks/useTipWallet';
import { useBCHPrice, getBCHPrice } from '../hooks/useBCHPrice';
import { prefetchBCHAddress, getCachedBCHAddress } from '../hooks/useBCHAddress';
import { FLAG_KIND, FLAG_TAG, FLAG_THRESHOLD, isBanned } from '../lib/communityFlag';
import { OPERATOR_PUBKEY } from '../lib/platform';
import {
  BOOST_KIND, BOOST_D_TAG_PREFIX, BOOST_WALLET, CLIP_BOOST_TAG, CLIP_BOOST_DURATIONS,
  BOOST_MIN_BID_USD, BOOST_BID_PRESETS, usdToSats, satsToUsdStr,
  isBoostActive, parseBoostData, sortBoostsByBid, type BoostData,
} from '../lib/boost';

// ── On-screen debug console (Eruda) ────────────────────────────────
// Loads when ?debug=true is in the URL. Useful for debugging on iPhone
// without needing a Mac + Safari Web Inspector.
// Usage: https://bchnostr.com/clips?debug=true
let erudaLoaded = false;
function loadEruda() {
  if (erudaLoaded) return;
  erudaLoaded = true;
  const script = document.createElement('script');
  script.src = 'https://cdn.jsdelivr.net/npm/eruda';
  script.onload = () => {
    (window as any).eruda?.init();
  };
  document.head.appendChild(script);
}

// ── Video-optimized relays ──────────────────────────────────────────
const VIDEO_RELAYS = [
  'wss://relay.divine.video',
  'wss://relay.primal.net',
];

const VIDEO_KINDS = [22, 21, 34236, 34235];
const BCH_TAGS = ['bch', 'bitcoincash', 'cashclips', 'cashtokens'];

// ── Helpers ─────────────────────────────────────────────────────────

function formatCount(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}

const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isWebKit = typeof navigator !== 'undefined' && /AppleWebKit/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent);

// ── Video Debug Logger ──────────────────────────────────────────────
// Comprehensive logging for diagnosing iOS playback failures.
// All events are prefixed with [ClipVideo] for easy filtering.

const VIDEO_DEBUG = true; // Set to false to silence in production

const VIDEO_EVENTS_TO_TRACK = [
  'loadstart', 'loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough',
  'play', 'playing', 'pause', 'stalled', 'waiting', 'error',
  'suspend', 'abort', 'emptied', 'ended', 'seeked', 'seeking',
] as const;

/** Map MediaError.code to human-readable name */
function mediaErrorName(code: number | undefined): string {
  switch (code) {
    case 1: return 'MEDIA_ERR_ABORTED';
    case 2: return 'MEDIA_ERR_NETWORK';
    case 3: return 'MEDIA_ERR_DECODE';
    case 4: return 'MEDIA_ERR_SRC_NOT_SUPPORTED';
    default: return `UNKNOWN(${code})`;
  }
}

/** Extract readable state from a video element */
function videoState(v: HTMLVideoElement): Record<string, any> {
  return {
    readyState: v.readyState,
    networkState: v.networkState,
    paused: v.paused,
    ended: v.ended,
    currentTime: v.currentTime,
    duration: isNaN(v.duration) ? 'NaN' : v.duration,
    buffered: v.buffered.length > 0
      ? `${v.buffered.start(0).toFixed(1)}-${v.buffered.end(v.buffered.length - 1).toFixed(1)}s`
      : 'empty',
    videoWidth: v.videoWidth,
    videoHeight: v.videoHeight,
    src: v.currentSrc || v.src || '(none)',
    error: v.error ? `${mediaErrorName(v.error.code)}: ${v.error.message || '(no message)'}` : null,
  };
}

function clipLog(tag: string, url: string, data?: any) {
  if (!VIDEO_DEBUG) return;
  const shortUrl = url.length > 80 ? url.slice(0, 40) + '...' + url.slice(-30) : url;
  if (data) {
    console.log(`[ClipVideo][${tag}] ${shortUrl}`, data);
  } else {
    console.log(`[ClipVideo][${tag}] ${shortUrl}`);
  }
}

function clipWarn(tag: string, url: string, data?: any) {
  const shortUrl = url.length > 80 ? url.slice(0, 40) + '...' + url.slice(-30) : url;
  if (data) {
    console.warn(`[ClipVideo][${tag}] ${shortUrl}`, data);
  } else {
    console.warn(`[ClipVideo][${tag}] ${shortUrl}`);
  }
}

/**
 * Attach all debug event listeners to a video element.
 * Returns a cleanup function.
 */
function attachVideoDebugListeners(video: HTMLVideoElement, clipUrl: string): () => void {
  if (!VIDEO_DEBUG) return () => {};

  const handlers: Array<[string, EventListener]> = [];

  for (const eventName of VIDEO_EVENTS_TO_TRACK) {
    const handler = () => {
      if (eventName === 'error') {
        const err = video.error;
        clipWarn('ERROR', clipUrl, {
          code: err?.code,
          name: mediaErrorName(err?.code),
          message: err?.message || '(no message)',
          ...videoState(video),
        });
      } else if (eventName === 'stalled' || eventName === 'waiting' || eventName === 'suspend' || eventName === 'abort') {
        clipWarn(eventName.toUpperCase(), clipUrl, videoState(video));
      } else {
        clipLog(eventName, clipUrl, eventName === 'loadedmetadata' || eventName === 'canplay' ? videoState(video) : undefined);
      }
    };
    video.addEventListener(eventName, handler);
    handlers.push([eventName, handler]);
  }

  return () => {
    for (const [name, fn] of handlers) {
      video.removeEventListener(name, fn);
    }
  };
}

/**
 * Probe a video URL for iOS compatibility.
 * Checks CORS headers, byte-range support, Content-Type.
 * Logs findings. Non-blocking — does not affect playback.
 * NOTE: Uses no-cors for HEAD (many video servers don't support CORS on HEAD).
 * The range request uses cors mode to test actual CORS support.
 */
async function probeVideoUrl(url: string): Promise<void> {
  if (!VIDEO_DEBUG) return;
  try {
    // Use no-cors for HEAD — we won't see headers but we can check
    // if the request succeeds at all. Then do a cors-mode Range request
    // to test actual streaming compatibility.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 6000);

    // Try cors mode first — if the server supports it we get full headers
    let res: Response;
    let corsMode = true;
    try {
      res = await fetch(url, { method: 'HEAD', signal: controller.signal, mode: 'cors' });
    } catch {
      // CORS blocked — try no-cors (opaque response, no headers visible)
      corsMode = false;
      res = await fetch(url, { method: 'HEAD', signal: controller.signal, mode: 'no-cors' });
    }
    clearTimeout(timeout);

    if (corsMode) {
      const contentType = res.headers.get('content-type') || '(missing)';
      const acceptRanges = res.headers.get('accept-ranges') || '(missing)';
      const contentLength = res.headers.get('content-length') || '(missing)';
      const cors = res.headers.get('access-control-allow-origin') || '(missing)';

      const issues: string[] = [];
      if (!contentType.includes('video/')) issues.push(`Content-Type is "${contentType}" — should be video/mp4`);
      if (acceptRanges === '(missing)' || acceptRanges === 'none') issues.push('Accept-Ranges missing on HEAD (may still work for GET)');
      if (cors === '(missing)') issues.push('No CORS on HEAD — do NOT use crossOrigin attribute');

      if (issues.length > 0) {
        clipWarn('PROBE:ISSUES', url, { contentType, acceptRanges, contentLength, cors, issues });
      } else {
        clipLog('PROBE:OK', url, { contentType, acceptRanges, contentLength, cors });
      }
    } else {
      clipLog('PROBE:NO_CORS', url, { note: 'Server does not support CORS on HEAD — this is normal. Do NOT use crossOrigin attr.' });
    }
  } catch (err) {
    clipWarn('PROBE:FAILED', url, { error: err instanceof Error ? err.message : String(err) });
  }
}

// ── Clip cache ──────────────────────────────────────────────────────

const CLIP_CACHE_KEY = 'bchnostr:clips:v3';
// With a busy feed, a returning visitor should get instant content, so we
// keep the cache usable for longer and store more clips. Stale cached clips
// only ever seed the FIRST paint — the live relay query still runs in
// parallel and replaces/extends them within a couple of seconds.
const CLIP_CACHE_TTL = 6 * 60 * 60 * 1000; // 6h
const CLIP_CACHE_SIZE = 60;

interface ClipCache { events: Event[]; ts: number; }

function getCachedClipEvents(): Event[] {
  try {
    const raw = localStorage.getItem(CLIP_CACHE_KEY);
    if (!raw) return [];
    const parsed: ClipCache = JSON.parse(raw);
    if (Date.now() - parsed.ts > CLIP_CACHE_TTL) return [];
    return parsed.events || [];
  } catch { return []; }
}

function setCachedClipEvents(events: Event[]) {
  try {
    localStorage.setItem(CLIP_CACHE_KEY, JSON.stringify({ events: events.slice(0, CLIP_CACHE_SIZE), ts: Date.now() }));
  } catch {}
}

// ── Bad URL cache ───────────────────────────────────────────────────
// Remembers video URLs that failed with format/decode errors so they
// get filtered out on subsequent visits. Self-cleaning: max 200 entries,
// entries expire after 30 days.

const BAD_URL_KEY = 'bchnostr:clips:bad-urls';
const BAD_URL_MAX = 200;
const BAD_URL_TTL = 30 * 86400 * 1000; // 30 days

interface BadUrlEntry { url: string; ts: number; }

let badUrlCache: Set<string> | null = null;

function loadBadUrls(): Set<string> {
  if (badUrlCache) return badUrlCache;
  try {
    const raw = localStorage.getItem(BAD_URL_KEY);
    if (!raw) { badUrlCache = new Set(); return badUrlCache; }
    const entries: BadUrlEntry[] = JSON.parse(raw);
    const now = Date.now();
    // Filter out expired entries
    const valid = entries.filter(e => now - e.ts < BAD_URL_TTL);
    badUrlCache = new Set(valid.map(e => e.url));
    // Clean up expired entries from storage
    if (valid.length < entries.length) {
      saveBadUrls(valid);
    }
    return badUrlCache;
  } catch { badUrlCache = new Set(); return badUrlCache; }
}

function saveBadUrls(entries: BadUrlEntry[]) {
  try {
    localStorage.setItem(BAD_URL_KEY, JSON.stringify(entries.slice(-BAD_URL_MAX)));
  } catch {}
}

function markBadUrl(url: string) {
  const cache = loadBadUrls();
  if (cache.has(url)) return;
  cache.add(url);
  // Append to stored list
  try {
    const raw = localStorage.getItem(BAD_URL_KEY);
    const entries: BadUrlEntry[] = raw ? JSON.parse(raw) : [];
    entries.push({ url, ts: Date.now() });
    saveBadUrls(entries);
  } catch {}
  if (VIDEO_DEBUG) clipLog('BAD_URL_CACHED', url, { cacheSize: cache.size });
}

function isBadUrl(url: string): boolean {
  return loadBadUrls().has(url);
}

// ── divine.video helpers ────────────────────────────────────────────

const DIVINE_HOST = 'media.divine.video';
const DIVINE_UPLOAD_URL = `https://${DIVINE_HOST}/upload`;

function getDivineHash(url: string): string | null {
  try {
    const u = new URL(url);
    if (!u.hostname.endsWith('divine.video')) return null;
    const seg = u.pathname.split('/').pop() || '';
    const hash = seg.replace(/\.\w+$/, '');
    return /^[a-f0-9]{64}$/i.test(hash) ? hash : null;
  } catch { return null; }
}

function getDivineThumbnailUrl(hash: string): string {
  return `https://${DIVINE_HOST}/${hash}.jpg`;
}

function getDivine480pUrl(hash: string): string {
  return `https://${DIVINE_HOST}/${hash}/480p`;
}

function getDivine720pUrl(hash: string): string {
  return `https://${DIVINE_HOST}/${hash}/720p`;
}

/**
 * Ensure a URL has a .mp4 extension for iOS Safari.
 * iOS Safari relies on file extensions in the URL path to detect media type.
 * Blossom URLs are bare SHA-256 hashes — iOS won't play them without this.
 */
function ensureVideoExtension(url: string): string {
  try {
    const u = new URL(url);
    if (/\.(mp4|webm|mov|m3u8|m4v)(\?.*)?$/i.test(u.pathname)) return url;
    if (/^\/[a-f0-9]{64}$/i.test(u.pathname)) {
      u.pathname += '.mp4';
      return u.toString();
    }
    return url;
  } catch { return url; }
}

/**
 * Get the best playback URL for the current device.
 * iOS always gets the original with .mp4 extension hint (Safari's hardware
 * decoder is fast and doesn't need transcoded variants).
 */
function getPlaybackUrl(videoUrl: string): { playUrl: string; thumbUrl?: string } {
  const hash = getDivineHash(videoUrl);
  if (!hash) {
    return { playUrl: ensureVideoExtension(videoUrl) };
  }
  if (isIOS) {
    return { playUrl: ensureVideoExtension(videoUrl), thumbUrl: getDivineThumbnailUrl(hash) };
  }
  return {
    playUrl: isMobile ? getDivine480pUrl(hash) : getDivine720pUrl(hash),
    thumbUrl: getDivineThumbnailUrl(hash),
  };
}

// ── HLS (.m3u8) support ─────────────────────────────────────────────
//
// Many short-video hosts serve HLS playlists. Safari (iOS + macOS) plays
// .m3u8 natively, but Chrome / Firefox / Android WebView do NOT — a raw
// <video src="…m3u8"> there fails with MEDIA_ERR_SRC_NOT_SUPPORTED (code 4),
// which surfaced as "This video format isn't supported" on a lot of clips.
// For those browsers we attach hls.js (the same engine BCH Live uses), lazily
// loaded from esm.sh only when an HLS clip is actually encountered.

function isHlsUrl(url: string): boolean {
  return /\.m3u8(\?|$)/i.test(url);
}

/** True when the browser can play HLS from a plain <video src> (Safari). */
function canPlayHlsNatively(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const v = document.createElement('video');
    return v.canPlayType('application/vnd.apple.mpegurl') !== ''
      || v.canPlayType('application/x-mpegURL') !== '';
  } catch { return false; }
}

// Cache the hls.js class across all clips so we import it at most once.
let hlsModulePromise: Promise<unknown> | null = null;
function loadHlsClass(): Promise<unknown> {
  if (!hlsModulePromise) {
    hlsModulePromise = import(/* @vite-ignore */ 'https://esm.sh/hls.js@1.5.17?target=esnext')
      .then(m => (m as { default: unknown }).default)
      .catch((err) => { hlsModulePromise = null; throw err; });
  }
  return hlsModulePromise;
}

// ── Types ──────────────────────────────────────────────────────────

interface Clip {
  event: Event;
  videoUrl: string;
  playbackUrl: string;
  thumbnailUrl?: string;
  title?: string;
  description: string;
  pubkey: string;
  mime?: string;
}

// ── Extract clips from events ──────────────────────────────────────

function extractClipFromEvent(event: Event): Clip | null {
  let videoUrl = '';
  let thumbnailUrl = '';
  let mime = '';

  for (const tag of event.tags) {
    if (tag[0] !== 'imeta') continue;
    let url = '', m = '', image = '';
    for (const v of tag) {
      if (v.startsWith('url ')) url = v.slice(4);
      if (v.startsWith('m ')) m = v.slice(2);
      if (v.startsWith('image ')) image = v.slice(6);
    }
    if (m.startsWith('video/') || /\.(mp4|webm|mov|m3u8)(\?.*)?$/i.test(url)) {
      videoUrl = url; mime = m;
      if (image) thumbnailUrl = image;
      break;
    }
  }

  if (!videoUrl) {
    const urlTag = event.tags.find(t => t[0] === 'url');
    if (urlTag?.[1] && /\.(mp4|webm|mov|m3u8)(\?.*)?$/i.test(urlTag[1])) {
      videoUrl = urlTag[1];
    }
  }

  if (!thumbnailUrl) {
    const thumbTag = event.tags.find(t => t[0] === 'thumb' || t[0] === 'image' || t[0] === 'preview');
    if (thumbTag?.[1]) thumbnailUrl = thumbTag[1];
  }

  if (!videoUrl) {
    const videoMatch = event.content.match(/(https?:\/\/[^\s]+\.(mp4|webm|mov|m3u8)(\?[^\s]*)?)/i);
    if (videoMatch) videoUrl = videoMatch[1];
  }

  if (!videoUrl) return null;

  // ── Validate URL is absolute HTTP(S) ──
  // Relative URLs or garbage strings would resolve against the page URL
  // (e.g. "clips" → "https://bchnostr.com/clips"), causing iOS Safari
  // to try to play an HTML page as video → MEDIA_ERR_SRC_NOT_SUPPORTED.
  if (!/^https?:\/\//i.test(videoUrl)) {
    if (VIDEO_DEBUG) clipWarn('INVALID_URL', videoUrl, { eventId: event.id, kind: event.kind });
    return null;
  }

  const title = event.tags.find(t => t[0] === 'title')?.[1];
  const optimized = getPlaybackUrl(videoUrl);
  const finalThumb = thumbnailUrl || optimized.thumbUrl;

  return {
    event, videoUrl, playbackUrl: optimized.playUrl,
    thumbnailUrl: finalThumb, title,
    description: event.content || '',
    pubkey: event.pubkey, mime,
  };
}

// ── Main Page ──────────────────────────────────────────────────────

export default function CashClipsPage() {
  const { user } = useAppContext();
  const [searchParams] = useSearchParams();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollingRef = useRef(false);

  // Load on-screen debug console when ?debug=true
  useEffect(() => {
    if (searchParams.get('debug') === 'true') loadEruda();
  }, [searchParams]);

  const [cachedEvents] = useState(() => getCachedClipEvents());

  // ── Primary: BCH-tagged short videos ──
  // Prioritised because they're explicitly BCH-relevant. Kept first in the
  // merged feed by the sort below. A `since` bound is essential now the feed
  // is busy: without it relays scan their entire history to satisfy the tag
  // filter before EOSE, which is exactly what kept the spinner up. 90 days is
  // plenty of BCH clips and lets relays answer far sooner.
  const [bchSince] = useState(() => Math.floor(Date.now() / 1000) - 90 * 86400);
  const { events: shortVideoEvents, loading: loadingShorts } = useNostrQuery({
    filters: [{ kinds: VIDEO_KINDS, '#t': BCH_TAGS, since: bchSince, limit: 60 }],
    allRelays: true,
    extraRelays: VIDEO_RELAYS,
  });

  // ── Fallback: recent short videos, untagged ──
  // Hashtags are only "Recommended" on kind 22 (NIP-71), so most short videos
  // carry none — a #t-only query leaves the feed empty. This second query
  // pulls the general short-video firehose so Cash Clips always has content,
  // while the BCH-tagged query above still surfaces BCH clips first. A tighter
  // window here (the firehose is huge) gets first clips on screen faster; the
  // 90-day BCH query above still backfills the long tail of BCH content.
  const [shortsSince] = useState(() => Math.floor(Date.now() / 1000) - (isMobile ? 7 : 14) * 86400);
  const { events: recentShortEvents, loading: loadingRecent } = useNostrQuery({
    filters: [{ kinds: [22, 34236], since: shortsSince, limit: isMobile ? 50 : 80 }],
    allRelays: true,
    extraRelays: VIDEO_RELAYS,
  });

  const [since] = useState(() => Math.floor(Date.now() / 1000) - (isMobile ? 3 : 7) * 86400);
  const { events: videoNotes } = useNostrQuery({
    filters: [{ kinds: [1], '#t': ['bch', 'bitcoincash'], limit: isMobile ? 15 : 30, since }],
  });

  // ── Moderation: flags + NIP-09 deletions ──────────────────────────
  const [flagSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 90);
  const { events: flagEvents } = useNostrQuery({
    filters: [{ kinds: [FLAG_KIND], '#t': ['cashclips-flag'], since: flagSince, limit: 200 }],
    enabled: true,
  });
  const { events: deleteEvents } = useNostrQuery({
    filters: [{ kinds: [5], '#t': ['cashclips-delete'], since: flagSince, limit: 100 }],
    enabled: true,
  });

  const hiddenClipIds = useMemo(() => {
    const hidden = new Set<string>();
    // Operator flags = instant hide
    const flagCounts = new Map<string, Set<string>>();
    for (const ev of flagEvents) {
      const clipId = ev.tags?.find((t: string[]) => t[0] === 'e')?.[1];
      if (!clipId) continue;
      if (ev.pubkey === OPERATOR_PUBKEY) {
        hidden.add(clipId);
        continue;
      }
      if (!flagCounts.has(clipId)) flagCounts.set(clipId, new Set());
      flagCounts.get(clipId)!.add(ev.pubkey);
    }
    flagCounts.forEach((flaggers, clipId) => {
      if (flaggers.size >= FLAG_THRESHOLD) hidden.add(clipId);
    });
    // NIP-09 deletions by operator
    for (const ev of deleteEvents) {
      if (ev.pubkey !== OPERATOR_PUBKEY) continue;
      const eTags = ev.tags?.filter((t: string[]) => t[0] === 'e') || [];
      for (const tag of eTags) { if (tag[1]) hidden.add(tag[1]); }
    }
    return hidden;
  }, [flagEvents, deleteEvents]);

  const isOperator = user?.pubkey === OPERATOR_PUBKEY;

  const stableClipsRef = useRef<Clip[]>([]);
  const stableSeenRef = useRef(new Set<string>());
  const stableAddrRef = useRef(new Set<string>());

  const clips = useMemo(() => {
    const allEvents = [...cachedEvents, ...shortVideoEvents, ...recentShortEvents, ...videoNotes];
    const newClips: Clip[] = [];
    const isVideoKind = (k: number) => k === 22 || k === 21 || k === 34236 || k === 34235;
    // A clip is BCH-relevant if it carries one of our discovery hashtags.
    // These lead the feed so Cash Clips stays a BCH surface even though the
    // fallback query pulls the general short-video firehose.
    const isBCH = (e: Event) =>
      e.tags.some(t => t[0] === 't' && BCH_TAGS.includes((t[1] || '').toLowerCase()));
    const sorted = allEvents.sort((a, b) => {
      const aV = isVideoKind(a.kind), bV = isVideoKind(b.kind);
      if (aV && !bV) return -1;
      if (bV && !aV) return 1;
      const aB = isBCH(a), bB = isBCH(b);
      if (aB && !bB) return -1;
      if (bB && !aB) return 1;
      const aS = a.kind === 22 || a.kind === 34236;
      const bS = b.kind === 22 || b.kind === 34236;
      if (aS && !bS) return -1;
      if (bS && !aS) return 1;
      return b.created_at - a.created_at;
    });

    for (const event of sorted) {
      if (stableSeenRef.current.has(event.id)) continue;
      if (hiddenClipIds.has(event.id)) continue;
      if (isBanned(event.pubkey)) continue;
      if (event.kind === 34235 || event.kind === 34236) {
        const dTag = event.tags.find(t => t[0] === 'd')?.[1] || '';
        const addrKey = `${event.pubkey}:${dTag}`;
        if (stableAddrRef.current.has(addrKey)) continue;
        stableAddrRef.current.add(addrKey);
      }
      stableSeenRef.current.add(event.id);
      const clip = extractClipFromEvent(event);
      // Skip clips with known-bad video URLs
      if (clip && !isBadUrl(clip.videoUrl) && !isBadUrl(clip.playbackUrl)) newClips.push(clip);
    }

    if (newClips.length > 0) {
      stableClipsRef.current = [...stableClipsRef.current, ...newClips];
    }
    // Filter out hidden/banned clips and bad URLs (re-evaluated when flags change)
    return stableClipsRef.current.filter(
      c => !hiddenClipIds.has(c.event.id) && !isBanned(c.pubkey) && !isBadUrl(c.videoUrl) && !isBadUrl(c.playbackUrl)
    );
  }, [cachedEvents.length, shortVideoEvents.length, recentShortEvents.length, videoNotes.length, hiddenClipIds]);

  useEffect(() => {
    if (clips.length > 0) setCachedClipEvents(clips.map(c => c.event));
  }, [clips.length]);

  const loading = (loadingShorts || loadingRecent) && clips.length === 0;

  // ── Batch-fetch profiles ──
  const visiblePubkeys = useMemo(() => {
    return [...new Set(clips.slice(0, 10).map(c => c.pubkey))];
  }, [clips.length]);

  const { events: _batchProfiles } = useNostrQuery({
    filters: visiblePubkeys.length > 0 ? [{ kinds: [0], authors: visiblePubkeys, limit: 20 }] : [],
    enabled: visiblePubkeys.length > 0,
    allRelays: true,
  });

  // ── Deferred engagement queries ──
  const [engagementReady, setEngagementReady] = useState(false);
  useEffect(() => {
    if (clips.length > 0 && !engagementReady) {
      const t = setTimeout(() => setEngagementReady(true), 800);
      return () => clearTimeout(t);
    }
  }, [clips.length, engagementReady]);

  const visibleEventIds = useMemo(() => clips.slice(0, 20).map(c => c.event.id), [clips.length]);

  const { events: batchReactions } = useNostrQuery({
    filters: visibleEventIds.length > 0 ? [{ kinds: [7], '#e': visibleEventIds, limit: 500 }] : [],
    enabled: engagementReady && visibleEventIds.length > 0,
  });

  const [tipSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 30);
  const { events: batchTips } = useNostrQuery({
    filters: visibleEventIds.length > 0 ? [{ kinds: [9735], '#e': visibleEventIds, limit: 200, since: tipSince }] : [],
    enabled: engagementReady && visibleEventIds.length > 0,
  });

  const likesMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of batchReactions) {
      const eTag = e.tags.find(t => t[0] === 'e');
      if (eTag) map.set(eTag[1], (map.get(eTag[1]) || 0) + 1);
    }
    return map;
  }, [batchReactions.length]);

  const userLikesSet = useMemo(() => {
    if (!user) return new Set<string>();
    const set = new Set<string>();
    for (const e of batchReactions) {
      if (e.pubkey === user.pubkey) {
        const eTag = e.tags.find(t => t[0] === 'e');
        if (eTag) set.add(eTag[1]);
      }
    }
    return set;
  }, [batchReactions.length, user?.pubkey]);

  const tipsMap = useMemo(() => {
    const map = new Map<string, { count: number; totalSats: number }>();
    for (const e of batchTips) {
      const eTag = e.tags.find(t => t[0] === 'e');
      if (!eTag) continue;
      const eid = eTag[1];
      const existing = map.get(eid) || { count: 0, totalSats: 0 };
      existing.count++;
      const a = e.tags.find(t => t[0] === 'amount');
      if (a) existing.totalSats += parseInt(a[1]) || 0;
      map.set(eid, existing);
    }
    return map;
  }, [batchTips.length]);

  // ── Clip Boost queries ──
  const [clipBoostSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 2);
  const { events: clipBoostEvents, refetch: refetchClipBoosts } = useNostrQuery({
    filters: [{ kinds: [BOOST_KIND], '#t': [CLIP_BOOST_TAG], since: clipBoostSince, limit: 50 }],
    enabled: true,
    allRelays: true,
  });

  // Active clip boosts: eventId → BoostData
  const activeClipBoosts = useMemo(() => {
    const map = new Map<string, BoostData>();
    for (const ev of clipBoostEvents) {
      if (!isBoostActive(ev)) continue;
      const data = parseBoostData(ev);
      if (!data) continue;
      const existing = map.get(data.eventId);
      if (!existing || data.expiresAt > existing.expiresAt) {
        map.set(data.eventId, data);
      }
    }
    return map;
  }, [clipBoostEvents]);

  // Listen for boost-published event to refresh
  useEffect(() => {
    const handler = (e: any) => {
      if (e.detail?.type === 'clip-boost') {
        refetchClipBoosts();
        // Also refetch after delays for relay propagation
        setTimeout(() => refetchClipBoosts(), 3000);
        setTimeout(() => refetchClipBoosts(), 8000);
      }
    };
    window.addEventListener('bchnostr:boost-published', handler);
    return () => window.removeEventListener('bchnostr:boost-published', handler);
  }, [refetchClipBoosts]);

  // Sort clips: boosted clips first (sorted by remaining time, most recent boost first), then regular
  const sortedClips = useMemo(() => {
    if (activeClipBoosts.size === 0) return clips;
    const boosted: Clip[] = [];
    const regular: Clip[] = [];
    for (const clip of clips) {
      if (activeClipBoosts.has(clip.event.id)) {
        boosted.push(clip);
      } else {
        regular.push(clip);
      }
    }
    // Sort boosted by bid amount (highest bidder gets top spot)
    boosted.sort((a, b) => {
      const aData = activeClipBoosts.get(a.event.id)!;
      const bData = activeClipBoosts.get(b.event.id)!;
      return bData.priceSats - aData.priceSats;
    });
    return [...boosted, ...regular];
  }, [clips, activeClipBoosts]);

  // ── Look-ahead preload ──
  // Warm the CURRENT clip plus the next 1–2 upcoming clips with
  // <link rel="preload" as="video">, so scrolling to the next clip finds its
  // first bytes already in the HTTP cache. This fetches ahead WITHOUT touching
  // any <video> element's load pipeline (which iOS Safari is fragile about —
  // see the notes on the <video> tag below). We keep the window small (2 ahead
  // on desktop, 1 on mobile) so a busy feed doesn't waste the viewer's data on
  // clips they may never reach, and we prune links for clips we've scrolled
  // past so at most a few preload requests are ever in flight.
  const preloadUrls = useMemo(() => {
    const ahead = isMobile ? 1 : 2;
    const urls: string[] = [];
    for (let i = currentIndex; i <= currentIndex + ahead && i < sortedClips.length; i++) {
      const u = sortedClips[i]?.playbackUrl;
      // Only preload plain progressive files; HLS (.m3u8) manifests are tiny
      // and handled by the player, and preloading them warms nothing useful.
      if (u && /^https?:\/\//i.test(u) && !/\.m3u8(\?|$)/i.test(u)) urls.push(u);
    }
    return urls;
  }, [currentIndex, sortedClips]);

  useEffect(() => {
    if (preloadUrls.length === 0) return;
    const wanted = new Set(preloadUrls);
    const created: HTMLLinkElement[] = [];

    // Add preload hints for any URL not already hinted.
    for (const url of preloadUrls) {
      const existing = document.querySelector(
        `link[data-clip-preload][href="${CSS.escape(url)}"]`,
      );
      if (existing) continue;
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'video';
      link.href = url;
      link.setAttribute('data-clip-preload', '');
      document.head.appendChild(link);
      created.push(link);
    }

    // Prune stale hints (clips we've now scrolled past) so we don't leave a
    // growing pile of preload requests competing for bandwidth.
    document.querySelectorAll('link[data-clip-preload]').forEach((el) => {
      const href = el.getAttribute('href') || '';
      if (!wanted.has(href)) { try { el.remove(); } catch { /* noop */ } }
    });

    return () => { for (const link of created) { try { link.remove(); } catch { /* noop */ } } };
  }, [preloadUrls]);

  // ── Scroll handling ──
  // Use IntersectionObserver to track which clip is visible.
  // This is far more reliable than scroll-position math on iOS where
  // momentum scrolling, rubber-banding, and snap settling all make
  // scrollTop-based calculations wrong.
  const observerRef = useRef<IntersectionObserver | null>(null);
  const clipElementsRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;

  useEffect(() => {
    if (!containerRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const idx = Number(entry.target.getAttribute('data-clip-index'));
            if (!isNaN(idx) && idx !== currentIndexRef.current) {
              setCurrentIndex(idx);
            }
          }
        }
      },
      {
        root: containerRef.current,
        threshold: 0.5,
      }
    );

    // Observe all currently registered elements
    clipElementsRef.current.forEach(el => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, [clips.length]);

  const registerClipRef = useCallback((el: HTMLDivElement | null, clipId: string) => {
    if (el) {
      clipElementsRef.current.set(clipId, el);
      observerRef.current?.observe(el);
    } else {
      const existing = clipElementsRef.current.get(clipId);
      if (existing) {
        observerRef.current?.unobserve(existing);
        clipElementsRef.current.delete(clipId);
      }
    }
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        goToClip(Math.min(currentIndex + 1, sortedClips.length - 1));
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        goToClip(Math.max(currentIndex - 1, 0));
      } else if (e.key === 'm') {
        setMuted(m => !m);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentIndex, sortedClips.length]);

  const goToClip = (index: number) => {
    if (!containerRef.current) return;
    scrollingRef.current = true;
    setCurrentIndex(index);
    const target = containerRef.current.children[index] as HTMLElement;
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setTimeout(() => { scrollingRef.current = false; }, 600);
  };

  // ── Layout fixup on mount ──
  // 1. Remove parent <main> bottom padding (we handle spacing ourselves)
  // 2. Measure actual available height and set CSS custom property
  useEffect(() => {
    const main = document.querySelector('main');
    const origPB = main?.style.paddingBottom || '';
    const origOF = main?.style.overflow || '';
    if (main) {
      main.style.paddingBottom = '0px';
      main.style.overflow = 'hidden';
    }

    // Measure the real available space for the clip feed.
    // This accounts for whatever headers/nav are actually rendered,
    // including iOS safe areas, PWA status bars, etc.
    const visibleHeight = (el: Element | null): number => {
      if (!el) return 0;
      // getComputedStyle().display === 'none' means the element takes no space.
      if (getComputedStyle(el).display === 'none') return 0;
      return el.getBoundingClientRect().height;
    };

    const measure = () => {
      const vh = window.innerHeight;

      // Our own sticky header inside the clips page.
      const headerEl = document.querySelector('.clips-header');
      const headerH = headerEl ? headerEl.getBoundingClientRect().height : 44;

      // Layout mobile header (hidden on desktop via lg:hidden).
      const mobileHeaderH = visibleHeight(document.querySelector('header'));

      // IMPORTANT: there are multiple <nav> elements in the DOM (the desktop
      // sidebar nav comes FIRST). We must target the fixed mobile bottom tab
      // bar specifically — querySelector('nav') would grab the sidebar and
      // wrongly subtract its full height, squashing the feed on desktop.
      const bottomNavH = visibleHeight(document.querySelector('nav[data-mobile-nav]'));

      // Desktop radio mini-player is fixed to the bottom when a track is
      // loaded. It overlaps the feed, so subtract its height too.
      const radioH = visibleHeight(document.querySelector('[data-desktop-radio]'));

      const feedH = vh - headerH - mobileHeaderH - bottomNavH - radioH;
      document.documentElement.style.setProperty('--clips-feed-h', `${Math.max(feedH, 200)}px`);
    };

    // Debounce measurements triggered by DOM mutations so we don't thrash
    // layout while the radio player (which mounts/unmounts as a fixed
    // bottom bar) or other late elements settle.
    let rafId = 0;
    const scheduleMeasure = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };

    // Measure now, on resize, and after short delays (for late-rendering
    // elements such as the desktop radio player).
    measure();
    const t1 = setTimeout(measure, 100);
    const t2 = setTimeout(measure, 400);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);

    // The desktop radio mini-player is added/removed from the DOM as tracks
    // start and stop. Watch the body so we re-measure when the bottom bar
    // appears or disappears while the user is on the clips feed.
    const mo = new MutationObserver(scheduleMeasure);
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      if (rafId) cancelAnimationFrame(rafId);
      mo.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      if (main) {
        main.style.paddingBottom = origPB;
        main.style.overflow = origOF;
      }
      // Clean up custom property
      document.documentElement.style.removeProperty('--clips-feed-h');
    };
  }, []);

  if (loading && clips.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] gap-4">
        <Film className="w-12 h-12 text-primary/30 animate-pulse" />
        <p className="text-sm text-muted-foreground">Loading Cash Clips...</p>
        <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (clips.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] gap-4 p-6 text-center">
        <Film className="w-16 h-16 text-muted-foreground/20" />
        <h2 className="text-lg font-bold">Cash Clips</h2>
        <p className="text-sm text-muted-foreground max-w-sm">
          No video clips found yet. Be the first to post a short video to the BCH community!
        </p>
        {user && (
          <button
            onClick={() => setShowUpload(true)}
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" /> Post a Clip
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="clips-page">
      {/* Header */}
      <div className="clips-header sticky top-0 z-30 bg-background/80 backdrop-blur-md border-b border-border px-4 py-2 flex items-center justify-between">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <Film className="w-5 h-5 text-primary" /> Cash Clips
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMuted(m => !m)}
            className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          {user && (
            <button
              onClick={() => setShowUpload(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground font-medium text-xs hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Post
            </button>
          )}
        </div>
      </div>

      {/* Clips feed — CSS-driven snap scroll.
          The height is set via CSS class `.clips-feed` in index.css
          using calc(100dvh - header - bottomnav) with env() for safe areas.
          NO JavaScript height measurement needed. */}
      <div
        ref={containerRef}
        className="clips-feed"
      >
        {sortedClips.map((clip, index) => {
          const distance = Math.abs(index - currentIndex);
          const shouldMountVideo = distance <= (isMobile ? 1 : 2);
          const boostData = activeClipBoosts.get(clip.event.id) || null;

          // Show thumbnail placeholder for clips that aren't mounted.
          // Always render the thumbnail (when available) so the snap target
          // has stable content and scrolling doesn't flash black between the
          // mounted-video window and the placeholder.
          if (!shouldMountVideo) {
            return (
              <div
                key={clip.event.id}
                data-clip-index={index}
                ref={(el) => registerClipRef(el, clip.event.id)}
                className="clips-item flex items-center justify-center"
              >
                {clip.thumbnailUrl && (
                  <img
                    src={clip.thumbnailUrl}
                    alt=""
                    className="w-full h-full object-contain opacity-70"
                    loading="lazy"
                  />
                )}
              </div>
            );
          }

          return (
            <ClipCard
              key={clip.event.id}
              clip={clip}
              index={index}
              isActive={index === currentIndex}
              isNearby={distance <= 1}
              muted={muted}
              onNext={() => goToClip(Math.min(index + 1, sortedClips.length - 1))}
              onPrev={() => goToClip(Math.max(index - 1, 0))}
              isFirst={index === 0}
              isLast={index === sortedClips.length - 1}
              likeCount={likesMap.get(clip.event.id) || 0}
              alreadyLiked={userLikesSet.has(clip.event.id)}
              tipInfo={tipsMap.get(clip.event.id) || null}
              registerRef={registerClipRef}
              isOperator={isOperator}
              boostData={boostData}
            />
          );
        })}
      </div>

      {showUpload && <UploadClipModal onClose={() => setShowUpload(false)} />}
    </div>
  );
}

// ── Clip Card ──────────────────────────────────────────────────────

function ClipCard({
  clip, index, isActive, muted, onNext, onPrev, isFirst, isLast, isNearby,
  likeCount: relayLikeCount, alreadyLiked, tipInfo, registerRef, isOperator, boostData,
}: {
  clip: Clip;
  index: number;
  isActive: boolean;
  muted: boolean;
  onNext: () => void;
  onPrev: () => void;
  isFirst: boolean;
  isLast: boolean;
  isNearby: boolean;
  likeCount: number;
  alreadyLiked: boolean;
  tipInfo: { count: number; totalSats: number } | null;
  registerRef: (el: HTMLDivElement | null, id: string) => void;
  isOperator?: boolean;
  boostData: BoostData | null;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [useFallbackUrl, setUseFallbackUrl] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const effectiveUrl = useFallbackUrl ? clip.videoUrl : clip.playbackUrl;
  const [localLiked, setLocalLiked] = useState(false);
  const liked = alreadyLiked || localLiked;
  const displayLikeCount = relayLikeCount + (localLiked && !alreadyLiked ? 1 : 0);
  const [showComments, setShowComments] = useState(false);
  const [showTipSheet, setShowTipSheet] = useState(false);
  const [reposted, setReposted] = useState(false);
  const [showPauseIcon, setShowPauseIcon] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [showBoostSheet, setShowBoostSheet] = useState(false);
  const [shared, setShared] = useState(false);
  const pauseTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const { profile } = useNostrProfile(clip.pubkey);
  // Track whether this component has ever been active, to avoid
  // re-probing the URL every time it becomes active again
  const probedRef = useRef(false);
  // Track the URL we set on the video element to avoid re-setting it
  // (which causes iOS to restart the load pipeline)
  const loadedUrlRef = useRef<string>('');
  // Active hls.js instance (only when the clip is HLS and the browser lacks
  // native HLS support). Torn down when the URL changes or the card unmounts.
  const hlsRef = useRef<{ destroy: () => void } | null>(null);

  // ── Attach debug event listeners (once, on mount) ──
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const cleanup = attachVideoDebugListeners(video, effectiveUrl);
    return cleanup;
  }, [effectiveUrl]);

  // ── Probe URL for iOS compatibility (once per clip) ──
  useEffect(() => {
    if (!isActive || probedRef.current) return;
    probedRef.current = true;
    probeVideoUrl(effectiveUrl);
  }, [isActive, effectiveUrl]);

  // ── Set video src directly (not via <source> element) ──
  // Using the `src` attribute directly is more reliable on iOS Safari
  // than <source> elements inside React. React's reconciliation can
  // remove/re-add <source> without calling load(), leaving iOS in a
  // broken state.
  //
  // HLS (.m3u8) is a special case: Safari plays it natively via a plain src,
  // but Chrome / Firefox / Android need hls.js attached to the element or the
  // load fails with MEDIA_ERR_SRC_NOT_SUPPORTED. We branch on that here.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    // Sanity check: never set a non-HTTP URL as video src.
    // Relative URLs resolve against the page and cause iOS to load HTML as video.
    if (!/^https?:\/\//i.test(effectiveUrl)) {
      clipWarn('SKIP_BAD_SRC', effectiveUrl, { reason: 'not an absolute HTTP URL' });
      return;
    }
    // Same URL already wired up — nothing to do (avoids iOS reload thrash).
    if (loadedUrlRef.current === effectiveUrl) return;

    // Tear down any previous hls.js instance before switching sources.
    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch { /* ignore */ }
      hlsRef.current = null;
    }

    // ── HLS on a browser without native support → hls.js ──
    if (isHlsUrl(effectiveUrl) && !canPlayHlsNatively()) {
      const urlAtStart = effectiveUrl;
      loadedUrlRef.current = effectiveUrl;
      clipLog('HLS_ATTACH', effectiveUrl);
      loadHlsClass().then((HlsClass) => {
        // Bail if the card was reused for a different clip meanwhile.
        if (loadedUrlRef.current !== urlAtStart || !videoRef.current) return;
        const H = HlsClass as {
          isSupported: () => boolean;
          new (opts: Record<string, unknown>): {
            loadSource: (u: string) => void;
            attachMedia: (el: HTMLVideoElement) => void;
            destroy: () => void;
          };
        };
        if (!H?.isSupported?.()) {
          // No MSE at all — last resort, let the element try natively.
          clipWarn('HLS_UNSUPPORTED', urlAtStart, { note: 'hls.js not supported; trying native' });
          videoRef.current.src = urlAtStart;
          return;
        }
        const h = new H({ enableWorker: false, maxBufferLength: 30, backBufferLength: 15 });
        hlsRef.current = h;
        h.loadSource(urlAtStart);
        h.attachMedia(videoRef.current);
      }).catch((err) => {
        clipWarn('HLS_LOAD_FAILED', urlAtStart, { error: err instanceof Error ? err.message : String(err) });
        // Fall back to a native attempt so Safari-like engines still work.
        if (videoRef.current && loadedUrlRef.current === urlAtStart) videoRef.current.src = urlAtStart;
      });
      return;
    }

    // ── Progressive (or native-HLS) source ──
    clipLog('SET_SRC', effectiveUrl, { previous: loadedUrlRef.current || '(none)' });
    video.src = effectiveUrl;
    loadedUrlRef.current = effectiveUrl;
    // Do NOT call video.load() — setting src already triggers it.
    // Calling load() again on iOS causes a double-reset of the
    // media element, which can leave it in a blank/black state.
  }, [effectiveUrl]);

  // Tear down hls.js when the card unmounts.
  useEffect(() => () => {
    if (hlsRef.current) {
      try { hlsRef.current.destroy(); } catch { /* ignore */ }
      hlsRef.current = null;
    }
  }, []);

  // ── Video playback: unified for all platforms ──
  // Play when active, pause when not. Start muted (autoplay policy).
  // On iOS, play() may still fail for unmuted audio without gesture —
  // we catch that and fall back to muted playback.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isActive) {
      clipLog('ACTIVATE', effectiveUrl, { readyState: video.readyState, networkState: video.networkState });

      const tryPlay = () => {
        video.muted = muted;
        clipLog('PLAY_ATTEMPT', effectiveUrl, { muted, readyState: video.readyState });
        const p = video.play();
        if (p) {
          p.then(() => {
            clipLog('PLAY_SUCCESS', effectiveUrl);
            setPlaying(true);
            setVideoError(null);
          }).catch((err) => {
            clipWarn('PLAY_BLOCKED', effectiveUrl, { error: err.message, muted });
            // Autoplay blocked (common on iOS for unmuted) — retry muted
            video.muted = true;
            video.play()
              .then(() => {
                clipLog('PLAY_SUCCESS_MUTED', effectiveUrl);
                setPlaying(true);
                setVideoError(null);
              })
              .catch((err2) => {
                clipWarn('PLAY_FAILED', effectiveUrl, {
                  error: err2.message,
                  ...videoState(video),
                });
                setPlaying(false);
                setVideoError(`Playback failed: ${err2.message}`);
              });
          });
        }
      };

      // If the video has enough data, play immediately.
      // Otherwise wait for it to be ready.
      // IMPORTANT: Do NOT call video.load() here. The src was already
      // set in the effect above, which triggers loading automatically.
      // Calling load() again on iOS resets the element and can cause
      // the black-screen issue.
      if (video.readyState >= 2) { // HAVE_CURRENT_DATA or better
        video.currentTime = 0;
        tryPlay();
      } else {
        clipLog('WAITING_FOR_DATA', effectiveUrl, { readyState: video.readyState });
        const onReady = () => {
          clipLog('DATA_READY', effectiveUrl, videoState(video));
          video.currentTime = 0;
          tryPlay();
        };
        video.addEventListener('canplay', onReady, { once: true });
        // Safety timeout: if canplay never fires (e.g. broken URL),
        // try playing anyway after 5s — iOS sometimes skips canplay.
        const safetyTimer = setTimeout(() => {
          video.removeEventListener('canplay', onReady);
          if (video.readyState >= 1) {
            clipWarn('SAFETY_PLAY', effectiveUrl, videoState(video));
            tryPlay();
          } else {
            clipWarn('SAFETY_TIMEOUT', effectiveUrl, videoState(video));
            setVideoError('Video failed to load');
          }
        }, 8000);
        return () => {
          video.removeEventListener('canplay', onReady);
          clearTimeout(safetyTimer);
        };
      }
    } else {
      if (!video.paused) {
        video.pause();
        clipLog('PAUSE', effectiveUrl);
      }
      setPlaying(false);
    }
  }, [isActive, muted, effectiveUrl]);

  // Sync muted state
  useEffect(() => {
    const video = videoRef.current;
    if (video) video.muted = muted;
  }, [muted]);

  // Buffering detection
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onWaiting = () => { setBuffering(true); clipLog('BUFFERING', effectiveUrl); };
    const onPlaying = () => { setBuffering(false); };
    const onCanPlayThrough = () => { setBuffering(false); };
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('canplaythrough', onCanPlayThrough);
    return () => {
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('canplaythrough', onCanPlayThrough);
    };
  }, [effectiveUrl]);

  // ── Error handling with fallback ──
  const handleVideoError = useCallback(() => {
    const video = videoRef.current;
    const err = video?.error;
    clipWarn('ERROR_HANDLER', effectiveUrl, {
      code: err?.code,
      name: mediaErrorName(err?.code),
      message: err?.message,
      useFallbackUrl,
      originalUrl: clip.playbackUrl,
      fallbackUrl: clip.videoUrl,
      ...videoState(video!),
    });
    if (!useFallbackUrl && clip.playbackUrl !== clip.videoUrl) {
      clipLog('TRYING_FALLBACK', clip.videoUrl);
      setUseFallbackUrl(true);
    } else {
      // Show user-friendly messages based on error type
      const msg = err?.message?.toLowerCase() || '';
      const code = err?.code;
      if (code === 4 || msg.includes('format') || msg.includes('not supported')) {
        // Cache this URL so it's skipped on future visits
        markBadUrl(clip.videoUrl);
        if (clip.playbackUrl !== clip.videoUrl) markBadUrl(clip.playbackUrl);
        setVideoError('This video format isn\'t supported on your device. The uploader may need to re-encode it as MP4 (H.264).');
      } else if (code === 2 || msg.includes('network')) {
        // Don't cache network errors — they're transient
        setVideoError('Network error — check your connection and try again.');
      } else if (code === 3 || msg.includes('decode')) {
        // Cache decode errors — the file itself is bad
        markBadUrl(clip.videoUrl);
        if (clip.playbackUrl !== clip.videoUrl) markBadUrl(clip.playbackUrl);
        setVideoError('This video couldn\'t be decoded. It may be corrupted.');
      } else {
        setVideoError(`Playback error — ${err?.message || 'unknown issue'}`);
      }
    }
  }, [effectiveUrl, useFallbackUrl, clip.playbackUrl, clip.videoUrl]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (playing) {
      video.pause();
      setPlaying(false);
      setShowPauseIcon(true);
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = setTimeout(() => setShowPauseIcon(false), 600);
    } else {
      video.muted = muted;
      video.play().then(() => setPlaying(true)).catch(() => {
        video.muted = true;
        video.play().then(() => setPlaying(true)).catch(() => {});
      });
    }
  }, [playing, muted]);

  const handleLike = async () => {
    if (!user || liked) return;
    setLocalLiked(true);
    try {
      await publish({
        kind: 7, content: '+',
        tags: [['e', clip.event.id], ['p', clip.pubkey]],
        created_at: Math.floor(Date.now() / 1000),
      });
    } catch {}
  };

  const handleRepost = async () => {
    if (!user || reposted) return;
    setReposted(true);
    try {
      const relayHint = getRelays()[0] || '';
      await publish({
        kind: 6, content: JSON.stringify(clip.event),
        tags: [['e', clip.event.id, relayHint], ['p', clip.pubkey]],
        created_at: Math.floor(Date.now() / 1000),
      });
    } catch { setReposted(false); }
  };

  // ── Report (community flag) ──
  const handleReport = async () => {
    if (!user || reporting || reported) return;
    setReporting(true);
    try {
      await publish({
        kind: FLAG_KIND,
        content: 'Inappropriate content',
        tags: [
          ['e', clip.event.id],
          ['p', clip.pubkey],
          ['t', 'cashclips-flag'],
          ['t', FLAG_TAG],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setReported(true);
    } catch {}
    setReporting(false);
  };

  // ── Operator remove (instant hide via flag + NIP-09 delete) ──
  const handleOperatorRemove = async () => {
    if (!user || removing || removed) return;
    setRemoving(true);
    try {
      // Publish operator flag (instant hide)
      await publish({
        kind: FLAG_KIND,
        content: 'Removed by operator — inappropriate content',
        tags: [
          ['e', clip.event.id],
          ['p', clip.pubkey],
          ['t', 'cashclips-flag'],
          ['t', FLAG_TAG],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      // Also publish NIP-09 deletion request for belt-and-suspenders
      await publish({
        kind: 5,
        content: 'Removed by operator',
        tags: [
          ['e', clip.event.id],
          ['t', 'cashclips-delete'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setRemoved(true);
    } catch {}
    setRemoving(false);
  };

  const name = profile?.display_name || profile?.name || clip.pubkey.slice(0, 8) + '...';
  const description = clip.description.replace(/https?:\/\/\S+/g, '').trim();

  // If removed by operator, show placeholder
  if (removed) {
    return (
      <div
        data-clip-index={index}
        ref={(el) => registerRef(el, clip.event.id)}
        className="clips-item relative bg-black flex items-center justify-center"
      >
        <div className="text-center">
          <ShieldX className="w-12 h-12 text-red-500/50 mx-auto mb-2" />
          <p className="text-sm text-white/60 font-medium">Clip removed</p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-clip-index={index}
      ref={(el) => registerRef(el, clip.event.id)}
      className="clips-item relative"
    >
      {/* Video — click directly on it for play/pause.
          No overlay div needed. This avoids iOS Safari issues where
          absolute-positioned elements inside scroll-snap items
          intercept touch gestures and break scrolling.

          iOS-critical notes:
          - Use `src` attribute directly, NOT <source> elements.
            React can remove/re-add <source> without calling load(),
            leaving iOS Safari's media pipeline in a broken state.
          - `src` is set via useEffect (not JSX) to control when it
            changes and avoid unnecessary reloads.
          - preload is fixed to "auto" for active/nearby clips.
            Changing preload during renders can trigger iOS to restart
            the load pipeline.
          - Do NOT use crossOrigin="anonymous" — divine.video does not
            return CORS headers on HEAD/preflight requests. iOS Safari
            will load metadata (which comes from the initial bytes) but
            then block the rest of the video data. We don't need CORS
            for simple playback; it's only needed for canvas/WebGL access. */}
      <video
        ref={videoRef}
        loop
        playsInline
        muted={muted}
        preload={isActive || isNearby ? 'auto' : 'metadata'}
        className="w-full h-full object-contain"
        poster={clip.thumbnailUrl}
        onClick={togglePlay}
        onError={handleVideoError}
      />

      {/* Video error overlay */}
      {videoError && isActive && (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-20 pointer-events-none">
          <div className="bg-black/80 rounded-2xl px-5 py-4 max-w-[300px] text-center pointer-events-auto">
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2.5" />
            <p className="text-sm text-white/90 font-medium mb-1">Can't play this clip</p>
            <p className="text-xs text-white/60 mb-3">{videoError}</p>
            <div className="flex items-center gap-2 justify-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setVideoError(null);
                  const video = videoRef.current;
                  if (video) {
                    clipLog('RETRY', effectiveUrl);
                    video.load();
                    video.play().catch(() => {});
                  }
                }}
                className="px-4 py-2 rounded-lg bg-white/20 text-white text-xs font-medium hover:bg-white/30 transition-colors"
              >
                Retry
              </button>
              {!isLast && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onNext();
                  }}
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
                >
                  Skip
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Play icon */}
      {!playing && isActive && !buffering && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
          <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center">
            <Play className="w-8 h-8 text-white translate-x-0.5" />
          </div>
        </div>
      )}

      {/* Pause icon flash */}
      {showPauseIcon && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
          <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center animate-[fadeIn_0.1s_ease-out]">
            <Pause className="w-8 h-8 text-white" />
          </div>
        </div>
      )}

      {/* Buffering */}
      {buffering && isActive && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
          <div className="w-12 h-12 border-3 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      )}

      {/* Boosted badge */}
      {boostData && (
        <div className="absolute top-3 left-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/90 backdrop-blur-sm">
          <Rocket className="w-3 h-3 text-white" />
          <span className="text-[10px] font-bold text-white">Boosted</span>
          <ClipBoostCountdown expiresAt={boostData.expiresAt} />
        </div>
      )}

      {/* Bottom overlay — user info */}
      <div className="absolute bottom-0 left-0 right-16 p-4 pb-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-20 pointer-events-none">
        <Link to={`/profile/${clip.pubkey}`} className="flex items-center gap-2 mb-2 pointer-events-auto">
          {profile?.picture ? (
            <img src={profile.picture} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-white/30" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center border-2 border-white/30">
              <User className="w-4 h-4 text-white" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-1">
              <p className="text-sm font-bold text-white drop-shadow-lg">{name}</p>
              <VerifiedBadge
                nip05={profile?.nip05}
                pubkey={clip.pubkey}
                size="xs"
                className="drop-shadow-lg"
              />
            </div>
            <p className="text-[10px] text-white/60">{timeAgo(clip.event.created_at)}</p>
          </div>
        </Link>
        {clip.title && <p className="text-sm font-semibold text-white drop-shadow-lg mb-1">{clip.title}</p>}
        {description && <p className="text-xs text-white/80 drop-shadow-md line-clamp-2">{description}</p>}
      </div>

      {/* Right sidebar — actions */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col items-center gap-4 z-20">
        <button onClick={handleLike} className="flex flex-col items-center gap-0.5">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${liked ? 'bg-red-500 text-white' : 'bg-black/40 text-white hover:bg-black/60'}`}>
            <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
          </div>
          <span className="text-[10px] text-white/80 font-medium drop-shadow-lg">
            {displayLikeCount > 0 ? formatCount(displayLikeCount) : 'Like'}
          </span>
        </button>

        <button onClick={() => setShowComments(!showComments)} className="flex flex-col items-center gap-0.5">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${showComments ? 'bg-primary text-white' : 'bg-black/40 text-white hover:bg-black/60'}`}>
            <MessageCircle className="w-5 h-5" />
          </div>
          <span className="text-[10px] text-white/80 font-medium drop-shadow-lg">Comments</span>
        </button>

        <button onClick={handleRepost} className="flex flex-col items-center gap-0.5">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${reposted ? 'bg-emerald-500 text-white' : 'bg-black/40 text-white hover:bg-black/60'}`}>
            {reposted ? <Check className="w-5 h-5" /> : <Repeat2 className="w-5 h-5" />}
          </div>
          <span className="text-[10px] text-white/80 font-medium drop-shadow-lg">{reposted ? 'Shared' : 'Repost'}</span>
        </button>

        <button
          onClick={async () => {
            const clipUrl = `${window.location.origin}/note/${clip.event.id}`;
            if (navigator.share) {
              try {
                await navigator.share({ title: clip.title || 'Cash Clip', url: clipUrl });
              } catch {}
            } else {
              try {
                await navigator.clipboard.writeText(clipUrl);
                setShared(true);
                setTimeout(() => setShared(false), 2000);
              } catch {}
            }
          }}
          className="flex flex-col items-center gap-0.5"
        >
          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${shared ? 'bg-primary text-white' : 'bg-black/40 text-white hover:bg-black/60'}`}>
            {shared ? <Check className="w-5 h-5" /> : <Share2 className="w-5 h-5" />}
          </div>
          <span className="text-[10px] text-white/80 font-medium drop-shadow-lg">{shared ? 'Copied!' : 'Share'}</span>
        </button>

        {user && clip.pubkey !== user.pubkey && (
          <button onClick={() => setShowTipSheet(true)} className="flex flex-col items-center gap-0.5">
            <div className="w-10 h-10 rounded-full bg-emerald-500/80 text-white flex items-center justify-center hover:bg-emerald-500 transition-colors">
              <Coins className="w-5 h-5" />
            </div>
            <span className="text-[10px] text-white/80 font-medium drop-shadow-lg">
              {tipInfo && tipInfo.count > 0 ? formatCount(tipInfo.count) : 'Tip'}
            </span>
          </button>
        )}

        {/* Boost button — creator only */}
        {user && clip.pubkey === user.pubkey && (
          <button onClick={() => setShowBoostSheet(true)} className="flex flex-col items-center gap-0.5">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              boostData ? 'bg-amber-500 text-white' : 'bg-amber-500/80 text-white hover:bg-amber-500'
            }`}>
              <Rocket className="w-5 h-5" />
            </div>
            <span className="text-[10px] text-white/80 font-medium drop-shadow-lg">
              {boostData ? 'Boosted' : 'Boost'}
            </span>
          </button>
        )}

        {/* Report button — any logged-in user can flag */}
        {user && clip.pubkey !== user.pubkey && !isOperator && (
          reported ? (
            <div className="flex flex-col items-center gap-0.5">
              <div className="w-10 h-10 rounded-full bg-amber-500/80 text-white flex items-center justify-center">
                <Check className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-white/80 font-medium drop-shadow-lg">Reported</span>
            </div>
          ) : showReport ? (
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={handleReport}
                disabled={reporting}
                className="w-10 h-10 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
              >
                {reporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-5 h-5" />}
              </button>
              <span className="text-[9px] text-white/80 font-bold drop-shadow-lg">Confirm?</span>
              <button onClick={() => setShowReport(false)} className="text-[9px] text-white/60 hover:text-white">
                Cancel
              </button>
            </div>
          ) : (
            <button onClick={() => setShowReport(true)} className="flex flex-col items-center gap-0.5">
              <div className="w-10 h-10 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition-colors">
                <Flag className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-white/80 font-medium drop-shadow-lg">Report</span>
            </button>
          )
        )}

        {/* Operator remove — instant removal */}
        {isOperator && (
          showRemoveConfirm ? (
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={handleOperatorRemove}
                disabled={removing}
                className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 transition-colors"
              >
                {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-5 h-5" />}
              </button>
              <span className="text-[9px] text-white font-bold drop-shadow-lg">Remove?</span>
              <button onClick={() => setShowRemoveConfirm(false)} className="text-[9px] text-white/60 hover:text-white">
                Cancel
              </button>
            </div>
          ) : (
            <button onClick={() => setShowRemoveConfirm(true)} className="flex flex-col items-center gap-0.5">
              <div className="w-10 h-10 rounded-full bg-red-500/60 text-white flex items-center justify-center hover:bg-red-500/80 transition-colors">
                <Shield className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-white/80 font-medium drop-shadow-lg">Remove</span>
            </button>
          )
        )}
      </div>

      {/* Navigation arrows (desktop) */}
      <div className="absolute right-2 top-4 hidden sm:flex flex-col gap-2 z-20">
        {!isFirst && (
          <button onClick={onPrev} className="w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition-colors">
            <ChevronUp className="w-4 h-4" />
          </button>
        )}
        {!isLast && (
          <button onClick={onNext} className="w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition-colors">
            <ChevronDown className="w-4 h-4" />
          </button>
        )}
      </div>

      {showComments && isActive && <CommentsSheet clip={clip} onClose={() => setShowComments(false)} />}
      {showTipSheet && isActive && (
        <ClipTipSheet
          recipientPubkey={clip.pubkey}
          recipientProfile={profile}
          eventId={clip.event.id}
          onClose={() => setShowTipSheet(false)}
        />
      )}
      {showBoostSheet && isActive && (
        <BoostClipSheet
          eventId={clip.event.id}
          existingBoost={boostData}
          onClose={() => setShowBoostSheet(false)}
        />
      )}
    </div>
  );
}

// ── Clip Boost Countdown ────────────────────────────────────────────

function ClipBoostCountdown({ expiresAt }: { expiresAt: number }) {
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const update = () => {
      const remaining = expiresAt - Math.floor(Date.now() / 1000);
      if (remaining <= 0) {
        if (spanRef.current) spanRef.current.textContent = '';
        return;
      }
      const hours = Math.floor(remaining / 3600);
      const mins = Math.floor((remaining % 3600) / 60);
      const label = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
      if (spanRef.current) spanRef.current.textContent = label;
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  return <span ref={spanRef} className="text-[10px] text-white/80 font-mono tabular-nums" />;
}

// ── Boost Clip Sheet (Auction Style) ────────────────────────────────

function BoostClipSheet({ eventId, existingBoost, onClose }: {
  eventId: string;
  existingBoost: BoostData | null;
  onClose: () => void;
}) {
  const { user } = useAppContext();
  const tipWallet = useTipWallet();
  const { price: bchPrice } = useBCHPrice();
  const { publish } = useNostrPublish();
  const [selectedDuration, setSelectedDuration] = useState(CLIP_BOOST_DURATIONS[0]); // default 6h
  const [bidUsd, setBidUsd] = useState(1);
  const [customInput, setCustomInput] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [boosting, setBoosting] = useState(false);
  const [boostSuccess, setBoostSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!tipWallet.ready) tipWallet.init().catch(() => {});
  }, []);

  // Fetch all active clip boosts for leaderboard
  const [boostSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 2);
  const { events: allClipBoosts } = useNostrQuery({
    filters: [{ kinds: [BOOST_KIND], '#t': [CLIP_BOOST_TAG], since: boostSince, limit: 50 }],
    enabled: true,
    allRelays: true,
  });

  // Leaderboard of active clip boosts
  const leaderboard = useMemo(() => {
    const active: BoostData[] = [];
    const seenEvents = new Set<string>();
    for (const ev of allClipBoosts) {
      if (!isBoostActive(ev)) continue;
      const data = parseBoostData(ev);
      if (!data || seenEvents.has(data.eventId)) continue;
      seenEvents.add(data.eventId);
      active.push(data);
    }
    return sortBoostsByBid(active);
  }, [allClipBoosts]);

  const bidSats = usdToSats(bidUsd, bchPrice);
  const minBidSats = usdToSats(BOOST_MIN_BID_USD, bchPrice);
  const walletBalance = Number(tipWallet.balanceSats);
  const canAfford = bidSats !== null && walletBalance >= bidSats;
  const bidTooLow = bidUsd < BOOST_MIN_BID_USD;
  const walletBalanceUSD = bchPrice ? walletBalance / 1e8 * bchPrice : 0;

  // Projected rank
  const projectedRank = useMemo(() => {
    if (!bidSats) return leaderboard.length + 1;
    const pos = leaderboard.findIndex(b => bidSats > b.priceSats);
    return pos >= 0 ? pos + 1 : leaderboard.length + 1;
  }, [bidSats, leaderboard]);

  const handlePreset = (usd: number) => {
    setBidUsd(usd);
    setShowCustom(false);
    setCustomInput('');
  };

  const handleCustomInput = (val: string) => {
    setCustomInput(val);
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) setBidUsd(Math.round(num * 100) / 100);
  };

  const handleBoost = async () => {
    if (!user || !canAfford || bidSats === null || bidTooLow) return;
    setBoosting(true);
    setError('');

    const now = Math.floor(Date.now() / 1000);
    const boostData: BoostData = {
      eventId,
      tier: 'bid',
      priceSats: bidSats,
      expiresAt: now + selectedDuration.duration,
      boostedBy: user.pubkey,
      bidUsd,
    };

    // Step 1: Publish boost event
    try {
      await publish({
        kind: BOOST_KIND,
        content: JSON.stringify(boostData),
        tags: [
          ['d', BOOST_D_TAG_PREFIX + 'clip/' + eventId],
          ['t', CLIP_BOOST_TAG],
          ['t', 'bch'],
          ['e', eventId],
          ['expires', String(now + selectedDuration.duration)],
          ['amount', String(bidSats)],
        ],
        created_at: now,
      });
    } catch (e) {
      setBoosting(false);
      setError(
        e instanceof Error && (e.message.includes('denied') || e.message.includes('reject'))
          ? 'Signing was denied. No BCH was charged.'
          : 'Failed to publish. No BCH was charged.'
      );
      return;
    }

    // Step 2: Send payment
    try {
      const txId = await tipWallet.sendBCH(BOOST_WALLET, BigInt(bidSats));

      // Re-publish with txid (best-effort)
      publish({
        kind: BOOST_KIND,
        content: JSON.stringify(boostData),
        tags: [
          ['d', BOOST_D_TAG_PREFIX + 'clip/' + eventId],
          ['t', CLIP_BOOST_TAG],
          ['t', 'bch'],
          ['e', eventId],
          ['expires', String(now + selectedDuration.duration)],
          ['amount', String(bidSats)],
          ['txid', txId],
        ],
        created_at: now + 1,
      }).catch(() => {});

      setBoostSuccess(true);
      tipWallet.refreshBalance();
      window.dispatchEvent(new CustomEvent('bchnostr:boost-published', { detail: { eventId, type: 'clip-boost' } }));
    } catch (e) {
      setError('Payment may have failed. Your boost event is published — check your wallet balance.');
      setBoostSuccess(true);
      window.dispatchEvent(new CustomEvent('bchnostr:boost-published', { detail: { eventId, type: 'clip-boost' } }));
    }
    setBoosting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        onClick={e => e.stopPropagation()}
        className="relative w-full max-h-[85vh] overflow-y-auto bg-card/95 backdrop-blur-xl border-t border-border rounded-t-2xl p-4 animate-[slideUp_0.2s_ease-out]"
        style={{ marginBottom: isMobile ? 'calc(56px + env(safe-area-inset-bottom, 0px))' : '0px', paddingBottom: isMobile ? '1rem' : '1.5rem' }}
      >
        {boostSuccess ? (
          <div className="flex flex-col items-center gap-2 py-4">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Rocket className="w-6 h-6 text-amber-500" />
            </div>
            <p className="text-sm font-bold text-amber-500">Clip Boosted! 🚀</p>
            <p className="text-xs text-muted-foreground text-center">
              Your clip is at the top of the feed for {selectedDuration.label.toLowerCase()}.
              {projectedRank <= 3 && (
                <span className="block mt-1 font-semibold text-amber-500">
                  Your bid landed at #{projectedRank} — top placement!
                </span>
              )}
            </p>
            {error && <p className="text-[10px] text-amber-600 text-center">{error}</p>}
            <button onClick={onClose} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium mt-1">
              Done
            </button>
          </div>
        ) : existingBoost ? (
          <div className="flex flex-col items-center gap-2 py-4">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Rocket className="w-6 h-6 text-amber-500" />
            </div>
            <p className="text-sm font-bold">This clip is already boosted</p>
            <p className="text-xs text-muted-foreground text-center">
              Your ${existingBoost.bidUsd?.toFixed(2) || satsToUsdStr(existingBoost.priceSats, bchPrice) || 'boost'} bid expires in{' '}
              <span className="font-semibold text-foreground">
                {(() => {
                  const remaining = Math.max(0, existingBoost.expiresAt - Math.floor(Date.now() / 1000));
                  if (remaining <= 0) return 'soon';
                  const h = Math.floor(remaining / 3600);
                  const m = Math.floor((remaining % 3600) / 60);
                  return h > 0 ? `${h}h ${m}m` : `${m}m`;
                })()}
              </span>.
              You can re-boost once it expires.
            </p>
            <button onClick={onClose} className="px-4 py-2 rounded-lg bg-muted text-foreground text-sm font-medium mt-1">
              Got it
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                  <Rocket className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold">Boost Clip</p>
                  <p className="text-[10px] text-muted-foreground">Name your price. Higher bids get top placement.</p>
                </div>
              </div>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            {/* Duration selection */}
            <div className="flex gap-2 mb-3">
              {CLIP_BOOST_DURATIONS.map(d => (
                <button
                  key={d.id}
                  onClick={() => setSelectedDuration(d)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold border-2 transition-all ${
                    selectedDuration.id === d.id
                      ? 'border-amber-500 bg-amber-500/10 text-amber-500'
                      : 'border-border text-muted-foreground hover:border-amber-500/40'
                  }`}
                >
                  <span>{d.emoji}</span> {d.label}
                </button>
              ))}
            </div>

            {/* Bid presets */}
            <div className="space-y-2 mb-3">
              <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-primary" />
                Your Bid
              </label>
              <div className="flex gap-1.5 flex-wrap">
                {BOOST_BID_PRESETS.map(p => (
                  <button
                    key={p.usd}
                    onClick={() => handlePreset(p.usd)}
                    className={`flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${
                      bidUsd === p.usd && !showCustom
                        ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary/30'
                        : 'border-border hover:border-primary/40 text-foreground'
                    }`}
                  >
                    <span>{p.emoji}</span>
                    <span>{p.label}</span>
                  </button>
                ))}
                <button
                  onClick={() => setShowCustom(!showCustom)}
                  className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${
                    showCustom
                      ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary/30'
                      : 'border-border hover:border-primary/40 text-muted-foreground'
                  }`}
                >
                  Custom
                </button>
              </div>

              {showCustom && (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-bold">$</span>
                  <input
                    type="number"
                    min={BOOST_MIN_BID_USD}
                    step="0.05"
                    value={customInput}
                    onChange={e => handleCustomInput(e.target.value)}
                    placeholder={`Min $${BOOST_MIN_BID_USD.toFixed(2)}`}
                    className="w-full pl-7 pr-3 py-2.5 rounded-xl border border-border bg-muted/50 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                    autoFocus
                  />
                </div>
              )}
            </div>

            {/* Bid summary */}
            <div className="rounded-xl bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/20 p-3 space-y-2 mb-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Your bid</span>
                <div className="text-right">
                  <span className="text-sm font-bold text-foreground">${bidUsd.toFixed(2)}</span>
                  <span className="text-[10px] text-muted-foreground font-mono ml-1.5">
                    {bidSats ? `~${bidSats.toLocaleString()} sats` : '...'}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Duration</span>
                <span className="text-xs font-semibold text-foreground">{selectedDuration.label}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Rank</span>
                <span className={`text-xs font-bold ${
                  projectedRank === 1 ? 'text-amber-500' :
                  projectedRank <= 3 ? 'text-primary' : 'text-foreground'
                }`}>
                  #{projectedRank}
                </span>
              </div>
              {bidTooLow && (
                <p className="text-[10px] text-red-500 font-medium">
                  Minimum bid is ${BOOST_MIN_BID_USD.toFixed(2)}
                </p>
              )}
            </div>

            {/* Active bids */}
            {leaderboard.length > 0 && (
              <div className="mb-3">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                  Active bids ({leaderboard.length})
                </p>
                <div className="rounded-xl border border-border overflow-hidden">
                  {leaderboard.slice(0, 5).map((b, i) => {
                    const remaining = b.expiresAt - Math.floor(Date.now() / 1000);
                    const h = Math.floor(remaining / 3600);
                    const m = Math.floor((remaining % 3600) / 60);
                    const timeLeft = h > 0 ? `${h}h ${m}m` : `${m}m`;
                    return (
                      <div key={b.eventId} className="flex items-center justify-between px-3 py-1.5 border-b border-border last:border-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-extrabold w-5 text-center ${
                            i === 0 ? 'text-amber-500' : i === 1 ? 'text-slate-400' : 'text-muted-foreground'
                          }`}>
                            #{i + 1}
                          </span>
                          <span className="text-xs font-bold text-foreground">
                            {satsToUsdStr(b.priceSats, bchPrice) || `${b.priceSats.toLocaleString()} sats`}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                          <Clock className="w-2.5 h-2.5" />
                          {timeLeft}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {leaderboard.length === 0 && (
              <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/20 px-3 py-2.5 mb-3">
                <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1.5">
                  <Rocket className="w-3 h-3" />
                  No active boosts — your clip will be #1!
                </p>
              </div>
            )}

            {/* Wallet */}
            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/50 mb-3">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Wallet className="w-3 h-3" /> Balance
              </span>
              <span className={`text-xs font-mono font-bold ${canAfford ? 'text-emerald-500' : bidSats ? 'text-red-500' : 'text-foreground'}`}>
                {tipWallet.loading ? '...' : walletBalanceUSD > 0 ? `$${walletBalanceUSD.toFixed(2)}` : `${walletBalance.toLocaleString()} sats`}
              </span>
            </div>

            {!canAfford && bidSats !== null && !bidTooLow && (
              <p className="text-[10px] text-red-500 mb-2 text-center">
                Not enough funds. Need {satsToUsdStr(bidSats - walletBalance, bchPrice) || `${(bidSats - walletBalance).toLocaleString()} sats`} more.
              </p>
            )}

            {error && <p className="text-xs text-red-500 mb-2 text-center">{error}</p>}

            {/* Boost button */}
            <button
              onClick={handleBoost}
              disabled={!tipWallet.ready || !canAfford || boosting || bidTooLow}
              className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                tipWallet.ready && canAfford && !bidTooLow
                  ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:opacity-90 active:scale-[0.98]'
                  : 'bg-muted text-muted-foreground cursor-not-allowed'
              } disabled:opacity-50`}
            >
              {boosting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Rocket className="w-4 h-4" />
              )}
              {boosting ? 'Boosting...' : `Boost for $${bidUsd.toFixed(2)} · ${selectedDuration.label}`}
            </button>

            <p className="text-[10px] text-muted-foreground text-center mt-2">
              Higher bids get top placement. Your bid competes with other active boosts.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ── Clip Tip Sheet ──────────────────────────────────────────────────

const TIP_PRESETS = [0.01, 0.05, 0.10, 0.25, 1, 5];
const TIP_RECEIPT_KIND = 9735;

function ClipTipSheet({ recipientPubkey, recipientProfile, eventId, onClose }: {
  recipientPubkey: string;
  recipientProfile: any;
  eventId: string;
  onClose: () => void;
}) {
  const { user } = useAppContext();
  const tipWallet = useTipWallet();
  const { price: bchPrice } = useBCHPrice();
  const { publish } = useNostrPublish();
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error' | 'no-funds' | 'no-address'>('idle');
  const [sentLabel, setSentLabel] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!tipWallet.ready) tipWallet.init().catch(() => {});
    prefetchBCHAddress(recipientPubkey);
  }, []);

  useEffect(() => {
    if (state === 'sent') {
      const t = setTimeout(onClose, 2000);
      return () => clearTimeout(t);
    }
  }, [state]);

  const handleTip = async (usdAmount: number) => {
    const price = getBCHPrice();
    if (!price) { setErrorMsg('BCH price unavailable'); setState('error'); return; }

    let bchAddress = getCachedBCHAddress(recipientPubkey);
    if (!bchAddress) bchAddress = await prefetchBCHAddress(recipientPubkey);
    if (!bchAddress) { setState('no-address'); return; }

    if (!tipWallet.ready) await tipWallet.init();

    const sats = BigInt(Math.round((usdAmount / price) * 1e8));
    if (tipWallet.balanceSats < sats + BigInt(300)) { setState('no-funds'); return; }

    setState('sending');
    setSentLabel(`$${usdAmount < 1 ? usdAmount.toFixed(2) : usdAmount}`);

    const TRANSIENT_RE = /electrum|network|timeout|ECONNRE|socket|mempool.*conflict|transaction.*already|failed to fetch|no response/i;
    const ADDRESS_RE = /cashaddr|decode|invalid.*address|checksum|non-base32|payload/i;

    let lastError = '';
    for (let attempt = 0; attempt <= 1; attempt++) {
      try {
        const txid = await tipWallet.sendTipWithFee(bchAddress, sats);
        setState('sent');
        publish({
          kind: TIP_RECEIPT_KIND,
          content: JSON.stringify({ amount_sats: Number(sats), amount_usd: usdAmount, txid: txid || '' }),
          tags: [['p', recipientPubkey], ['e', eventId], ['amount', Number(sats).toString()], ['t', 'bch-tip']],
          created_at: Math.floor(Date.now() / 1000),
        }).catch(() => {});
        tipWallet.refreshBalance();
        return;
      } catch (err) {
        lastError = err instanceof Error ? err.message : String(err);
        if (ADDRESS_RE.test(lastError)) break;
        if (attempt < 1 && TRANSIENT_RE.test(lastError)) {
          await new Promise(r => setTimeout(r, 2000));
          try { await tipWallet.init(); } catch {}
          continue;
        }
        break;
      }
    }
    setErrorMsg(lastError || 'Send failed');
    setState('error');
  };

  const walletBalanceUSD = bchPrice ? Number(tipWallet.balanceSats) / 1e8 * bchPrice : 0;
  const recipientName = recipientProfile?.display_name || recipientProfile?.name || 'this creator';

  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        className="w-full bg-card/95 backdrop-blur-xl border-t border-border rounded-t-2xl p-4 animate-[slideUp_0.2s_ease-out]"
        style={{ marginBottom: isMobile ? 'calc(56px + env(safe-area-inset-bottom, 0px))' : '0px', paddingBottom: isMobile ? '1rem' : '1.5rem' }}
      >
        {state === 'sent' ? (
          <div className="flex flex-col items-center gap-2 py-4">
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Check className="w-6 h-6 text-emerald-500" />
            </div>
            <p className="text-sm font-bold text-emerald-500">Sent {sentLabel}!</p>
          </div>
        ) : state === 'sending' ? (
          <div className="flex flex-col items-center gap-2 py-4">
            <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
            <p className="text-sm font-medium text-muted-foreground">Sending {sentLabel}...</p>
          </div>
        ) : state === 'error' ? (
          <div className="flex flex-col items-center gap-2 py-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
            <p className="text-xs text-red-500 text-center">{errorMsg}</p>
            <button onClick={() => setState('idle')} className="text-xs text-primary font-medium mt-1">Try again</button>
          </div>
        ) : state === 'no-funds' ? (
          <div className="flex flex-col items-center gap-2 py-4">
            <Wallet className="w-8 h-8 text-amber-500" />
            <p className="text-sm font-medium">Not enough funds</p>
            <p className="text-xs text-muted-foreground">Deposit BCH to your wallet in Settings.</p>
            <button onClick={onClose} className="text-xs text-primary font-medium mt-1">OK</button>
          </div>
        ) : state === 'no-address' ? (
          <div className="flex flex-col items-center gap-2 py-4">
            <AlertCircle className="w-8 h-8 text-amber-500" />
            <p className="text-sm font-medium">No BCH address</p>
            <p className="text-xs text-muted-foreground text-center">This user hasn't set up a BCH address yet.</p>
            <button onClick={onClose} className="text-xs text-primary font-medium mt-1">OK</button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Coins className="w-4 h-4 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm font-bold">Tip {recipientName}</p>
                  <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Wallet className="w-2.5 h-2.5" />
                    {tipWallet.loading ? '...' : walletBalanceUSD > 0 ? `$${walletBalanceUSD.toFixed(2)} balance` : `${Number(tipWallet.balanceSats).toLocaleString()} sats`}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {TIP_PRESETS.map(amount => {
                const sats = bchPrice ? Math.round((amount / bchPrice) * 1e8) : 0;
                const canAfford = tipWallet.ready && tipWallet.balanceSats >= BigInt(sats) + BigInt(300);
                return (
                  <button
                    key={amount}
                    onClick={() => handleTip(amount)}
                    disabled={!tipWallet.ready || !canAfford}
                    className={`flex flex-col items-center gap-0.5 py-3 rounded-xl border transition-all active:scale-95 ${
                      canAfford
                        ? 'border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/15 text-foreground'
                        : 'border-border/50 bg-muted/30 opacity-40 cursor-not-allowed'
                    }`}
                  >
                    <span className="text-base font-bold font-mono text-emerald-500">
                      ${amount < 1 ? amount.toFixed(2) : amount}
                    </span>
                    {sats > 0 && (
                      <span className="text-[9px] text-muted-foreground font-mono">
                        ~{sats.toLocaleString()} sats
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Reply Panel ────────────────────────────────────────────────────

function CommentsSheet({ clip, onClose }: { clip: Clip; onClose: () => void }) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [localComments, setLocalComments] = useState<any[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Fetch existing comments (legacy kind 1 + NIP-22 kind 1111 that reply to this clip)
  const { events: replyEvents, loading } = useNostrQuery({
    filters: [
      { kinds: [1], '#e': [clip.event.id], limit: 100 },
      { kinds: [1111], '#E': [clip.event.id], limit: 100 },
      { kinds: [1111], '#e': [clip.event.id], limit: 100 },
    ],
    enabled: true,
    allRelays: true,
  });

  const allComments = useMemo(() => {
    const seen = new Set<string>();
    const merged = [...replyEvents, ...localComments]
      .filter(e => {
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        return true;
      })
      .sort((a, b) => a.created_at - b.created_at);
    return merged;
  }, [replyEvents, localComments]);

  // Auto-scroll to bottom when new comments arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [allComments.length]);

  const handleSend = async () => {
    if (!user || !content.trim() || sending) return;
    setSending(true);
    try {
      const now = Math.floor(Date.now() / 1000);
      const clipKind = String(clip.event.kind || 22);
      const event = {
        kind: 1111,
        content: content.trim(),
        tags: [
          // NIP-22: root scope — the clip (uppercase)
          ['E', clip.event.id, '', clip.pubkey],
          ['K', clipKind],
          ['P', clip.pubkey],
          // NIP-22: parent item (same as root for top-level comments)
          ['e', clip.event.id, '', clip.pubkey],
          ['k', clipKind],
          ['p', clip.pubkey],
        ],
        created_at: now,
      };
      await publish(event);
      // Add to local comments so it shows immediately
      setLocalComments(prev => [...prev, {
        id: 'local-' + Date.now(),
        pubkey: user.pubkey,
        content: content.trim(),
        created_at: now,
        tags: event.tags,
      }]);
      setContent('');
    } catch (e) {
      console.error('Reply failed:', e);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      {/* Tap backdrop to close */}
      <div className="flex-1 min-h-[15%]" onClick={onClose} />

      {/* Comments sheet — sits above the mobile bottom nav */}
      <div
        className="bg-card/95 backdrop-blur-md border-t border-border rounded-t-2xl flex flex-col max-h-[70%] min-h-[50%]"
        style={{ marginBottom: isMobile ? 'calc(56px + env(safe-area-inset-bottom, 0px))' : '0px' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <h3 className="text-sm font-bold">{allComments.length > 0 ? `${allComments.length} comment${allComments.length !== 1 ? 's' : ''}` : 'Comments'}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Comments list */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-2 space-y-3">
          {loading && allComments.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            </div>
          )}

          {!loading && allComments.length === 0 && (
            <div className="text-center py-8">
              <MessageCircle className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">No comments yet</p>
              <p className="text-xs text-muted-foreground mt-0.5">Be the first to comment!</p>
            </div>
          )}

          {allComments.map(comment => (
            <CommentRow key={comment.id} event={comment} />
          ))}
        </div>

        {/* Reply input */}
        {user ? (
          <div className="border-t border-border px-3 py-2.5 shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1 bg-muted rounded-full px-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                onKeyDown={e => { if (e.key === 'Enter') handleSend(); if (e.key === 'Escape') onClose(); }}
              />
              <button
                onClick={handleSend}
                disabled={!content.trim() || sending}
                className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50 transition-colors shrink-0"
              >
                {sending ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        ) : (
          <div className="border-t border-border px-4 py-3 text-center shrink-0">
            <p className="text-xs text-muted-foreground">Log in to comment</p>
          </div>
        )}
      </div>
    </div>
  );
}

function CommentRow({ event }: { event: any }) {
  const { profile } = useNostrProfile(event.pubkey);
  const name = profile?.display_name || profile?.name || event.pubkey.slice(0, 8) + '...';

  return (
    <div className="flex gap-2.5">
      <Link to={`/profile/${event.pubkey}`} className="shrink-0">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="w-4 h-4 text-primary/60" />
          </div>
        )}
      </Link>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1.5">
          <Link to={`/profile/${event.pubkey}`} className="text-xs font-semibold hover:underline truncate">
            {name}
          </Link>
          <VerifiedBadge nip05={profile?.nip05} pubkey={event.pubkey} size="xs" />
          <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(event.created_at)}</span>
        </div>
        <p className="text-sm mt-0.5 whitespace-pre-wrap break-words">{event.content}</p>
      </div>
    </div>
  );
}

// ── Upload Clip Modal ──────────────────────────────────────────────

function UploadClipModal({ onClose }: { onClose: () => void }) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState<'uploading' | 'processing' | 'publishing'>('uploading');
  const [error, setError] = useState<string | null>(null);
  const [published, setPublished] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);

    // ── Enforce MP4 format ──
    // MP4 (H.264) is the only format that plays reliably on every device:
    // iOS Safari, Android, desktop. WebM/VP9/AV1 break on iOS.
    // MOV files are usually H.264 in a QuickTime container — divine.video
    // can transcode them, so we allow those too.
    const allowed = ['video/mp4', 'video/quicktime', 'video/x-m4v'];
    const ext = f.name.split('.').pop()?.toLowerCase();

    if (!allowed.includes(f.type) && ext !== 'mp4' && ext !== 'mov' && ext !== 'm4v') {
      setError(
        f.type.includes('webm')
          ? 'WebM videos don\'t play on iPhones. Please convert to MP4 first.'
          : `${f.type || ext?.toUpperCase() || 'This format'} isn't supported. Please use MP4 for maximum compatibility.`
      );
      return;
    }

    // ── Size check ──
    // Warn (but don't block) for very large files — they'll take forever to upload
    if (f.size > 200 * 1024 * 1024) {
      setError('Video is over 200 MB. Upload may be slow — consider trimming or compressing it.');
      // Don't return — let them proceed if they want
    }

    // ── Quick codec sniff for MP4 files ──
    // Read the first few bytes to verify it's actually an MP4 container
    // (not a .mp4 file that's secretly WebM or something else).
    // The MP4 container (ISO BMFF) starts with a "ftyp" box at offset 4.
    try {
      const header = await f.slice(0, 12).arrayBuffer();
      const view = new Uint8Array(header);
      // ftyp signature at bytes 4-7: 0x66 0x74 0x79 0x70
      const hasFtyp = view[4] === 0x66 && view[5] === 0x74 && view[6] === 0x79 && view[7] === 0x70;
      // WebM signature: bytes 0-3 = 0x1A 0x45 0xDF 0xA3
      const isWebM = view[0] === 0x1A && view[1] === 0x45 && view[2] === 0xDF && view[3] === 0xA3;

      if (isWebM) {
        setError('This file is actually WebM (not MP4). WebM doesn\'t play on iPhones. Please convert to MP4.');
        return;
      }
      if (!hasFtyp && f.type !== 'video/quicktime') {
        // Not a recognized MP4/MOV container — warn but allow (divine.video may handle it)
        setError('This file may not be a standard MP4. If upload fails, try converting to MP4 (H.264).');
      }
    } catch {
      // Can't read header — proceed anyway
    }

    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const uploadToDivine = async (videoFile: File): Promise<{ url: string; hash?: string }> => {
    const signer = (window as any).nostr;
    if (!signer) throw new Error('Not logged in');

    const pubkey = await signer.getPublicKey();
    const now = Math.floor(Date.now() / 1000);
    const buffer = await videoFile.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashHex = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');

    const authEvent = {
      kind: 24242, pubkey, created_at: now,
      tags: [['t', 'upload'], ['x', hashHex], ['expiration', String(now + 600)]],
      content: 'Upload video',
    };
    const signed = await signer.signEvent(authEvent);
    const authHeader = 'Nostr ' + btoa(JSON.stringify(signed));

    const response = await fetch(DIVINE_UPLOAD_URL, {
      method: 'PUT',
      headers: { 'Authorization': authHeader, 'Content-Type': 'video/mp4' },
      body: videoFile,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`divine.video: ${response.status} ${text.slice(0, 200)}`);
    }

    const data = await response.json().catch(() => null);
    const url = data?.url || data?.blob?.url;
    if (!url) throw new Error('No URL in divine.video response');

    return { url, hash: getDivineHash(url) || hashHex };
  };

  const handlePublish = async () => {
    if (!user || !file) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadStage('uploading');
    setError(null);

    try {
      let videoUrl: string;
      let divineHash: string | undefined;

      try {
        setUploadProgress(10);
        const result = await uploadToDivine(file);
        videoUrl = result.url;
        divineHash = result.hash;
        setUploadProgress(70);
      } catch (divineErr) {
        console.warn('[CashClips] divine.video upload failed, using fallback:', divineErr);
        videoUrl = await uploadVideo(file, (pct) => setUploadProgress(pct));
      }

      setUploadStage('processing');
      setUploadProgress(80);

      const imetaParts = ['imeta', `url ${videoUrl}`, `m video/mp4`];
      if (divineHash) imetaParts.push(`image ${getDivineThumbnailUrl(divineHash)}`);

      const tags: string[][] = [
        imetaParts,
        ['t', 'bch'],
        ['t', 'bitcoincash'],
        ['t', 'cashclips'],
      ];

      if (description.trim()) {
        const hashtags = description.match(/#(\w{2,})/g);
        if (hashtags) {
          hashtags.forEach(h => {
            const tag = h.slice(1).toLowerCase();
            if (tag !== 'bch' && tag !== 'bitcoincash' && tag !== 'cashclips') {
              tags.push(['t', tag]);
            }
          });
        }
      }

      setUploadStage('publishing');
      setUploadProgress(90);

      await publish({
        kind: 22,
        content: description.trim(),
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });

      setUploadProgress(100);
      setPublished(true);
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    return () => { if (preview) URL.revokeObjectURL(preview); };
  }, [preview]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2 border-b border-border">
          <h2 className="font-bold text-sm flex items-center gap-2">
            <Film className="w-4 h-4 text-primary" /> Post a Cash Clip
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {published ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                <Film className="w-8 h-8" />
              </div>
              <p className="text-sm font-semibold">Clip Published!</p>
              <p className="text-xs text-muted-foreground">Your Cash Clip is now live.</p>
            </div>
          ) : (
            <>
              {!file ? (
                <div className="space-y-3">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full aspect-[9/16] max-h-[300px] rounded-xl border-2 border-dashed border-border hover:border-primary/40 flex flex-col items-center justify-center gap-3 transition-colors"
                  >
                    <Camera className="w-10 h-10 text-muted-foreground/40" />
                    <div className="text-center">
                      <p className="text-sm font-medium">Select Video</p>
                      <p className="text-xs text-muted-foreground mt-1">MP4 or MOV only</p>
                    </div>
                  </button>
                  <div className="rounded-xl bg-muted/50 border border-border p-3 space-y-1.5">
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Tips for best results</p>
                    <ul className="text-[11px] text-muted-foreground space-y-1">
                      <li>• <strong className="text-foreground">MP4 format (H.264)</strong> — plays on every device including iPhone</li>
                      <li>• <strong className="text-foreground">15–60 seconds</strong> — short clips get more views</li>
                      <li>• <strong className="text-foreground">Vertical (9:16)</strong> — fills the screen on mobile</li>
                      <li>• <strong className="text-foreground">Under 200 MB</strong> — auto-transcoded to 720p &amp; 480p for fast playback</li>
                    </ul>
                    <p className="text-[10px] text-muted-foreground/60 mt-1.5">
                      No WebM or AV1 — these don't play on iPhones.
                    </p>
                    <p className="text-[10px] text-primary/70">
                      Powered by divine.video — thumbnails &amp; adaptive quality generated automatically
                    </p>
                  </div>
                </div>
              ) : (
                <div className="relative rounded-xl overflow-hidden bg-black">
                  <video src={preview!} controls playsInline className="w-full max-h-[300px] object-contain" />
                  <button
                    onClick={() => { setFile(null); setPreview(null); }}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              <input ref={fileInputRef} type="file" accept="video/mp4,video/quicktime,video/x-m4v,.mp4,.mov,.m4v" className="hidden" onChange={handleFileSelect} />

              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe your clip... #bch #cashclips"
                className="w-full bg-muted rounded-xl px-4 py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none min-h-[80px]"
                rows={3}
              />

              {uploading && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>
                      {uploadStage === 'uploading' && `Uploading to divine.video... ${uploadProgress}%`}
                      {uploadStage === 'processing' && 'Processing video (auto-transcoding)...'}
                      {uploadStage === 'publishing' && 'Publishing to Nostr...'}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  {uploadStage === 'processing' && (
                    <p className="text-[10px] text-muted-foreground">Video will be auto-transcoded to 720p &amp; 480p for fast mobile playback</p>
                  )}
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2">
                  <X className="w-3.5 h-3.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                onClick={handlePublish}
                disabled={!file || uploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {uploadStage === 'uploading' ? 'Uploading...' : uploadStage === 'processing' ? 'Processing...' : 'Publishing...'}
                  </>
                ) : (
                  <><Send className="w-4 h-4" /> Publish Clip</>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

